import React, { useEffect, useMemo, useRef, useState } from 'react';
import { WebComponentRenderer } from '../../../angular-in-react/WebComponentRenderer';
import { useConfirm } from '../../../modal/components/confirm-dialog-v1/ConfirmDialogV1Provider';
import { HdCoordinatedScrollAnchor } from '../../../a11y/scroll/components/coordinated-scroll/HdCoordinatedScroll';
import { MappingRow, MappingStatus, MappingSummary } from '../MappingBox/model';
import { NodeLogo } from '@hevoio/hevo-ui-toolkit';
import { HdButton } from '../../../components';
import { extendObject } from '../../../date-time/date-picker/utils';
import { QueryEditorBadgeType } from '../../../warehouse-management/workbench/interfaces/workbench';
import useAnalyticsTracker from '../../../analytics/hooks/useAnalyticsTracker';
import { useCanDeactivate } from '../../../routing/hooks/useCanDeactivate';
import useToaster from '../../../toaster/hooks/useToaster';
import { getDataIdGenerator } from '../../../core/utils/generateDataId';
import ActivateTargetAPI from '../../activate-target/ActivateTargetAPI';
import { Target } from '../../activate-target/models/target';
import { TARGET_TYPES, TargetTypesEnum } from '../../activate-target/models/target-type';
import { TargetFactory } from '../../activate-target/utils';
import { ActivationPreviewWorkbench } from '../ActivationPreviewWorkbench';
import ActivationsAPI from '../api/ActivationsAPI';
import { SyncHistoryItemFactory } from '../ActivationSyncHistory/factory';
import { ChangeMappingDialog } from '../ChangeMappingDialog';
import { transformMappingRowsToSheetRawData } from '../GoogleSheetsActivation/utils';
import { KlaviyoTargetObjectSettings } from '../KlaviyoActivation/target-object-settings';
import { Activation } from '../models/activation';
import { ACTIVATION_TARGET_META_DATA } from '../models/activation-target-meta-data';
import { GenericTargetObjectSettings } from '../models/target-object-settings';
import {
  TRACKER_ACTIVATION_OVERVIEW_CHANGE_MAPPING_OPEN,
  TRACKER_ACTIVATION_OVERVIEW_SAVE_DRAFT_QUERY_SUCCESS,
  TRACKER_ACTIVATION_OVERVIEW_SAVE_QUERY_CLICK,
  TRACKER_ACTIVATION_OVERVIEW_SAVE_QUERY_SUCCESS,
  TRACKER_ACTIVATION_OVERVIEW_SAVED_QUERY_REVERT_CLICK,
  TRACKER_ACTIVATION_OVERVIEW_SELECT_TAB_CLICK
} from '../tracking';
import { getMappingSummaryFromRawData, transformMappingRowsToRawData } from '../utils';
import { ActivationOverviewHeaderTarget } from './ActivationOverviewHeaderTarget';
import { DraftQueryMessagePane } from './DraftQueryMessagePane';
import { LastRunFailedRecordsMessagePane } from './LastRunFailedRecordsMessagePane';
import { ActivatePreviewTab } from './models';

export interface UpdateActivationMappingAngularInReactProps {
  activation: Activation;
  target: Target;
  onActivationUpdate: (activation: Activation) => any;
}

export function ActivationOverview({
  activation,
  setActivation,
  setWithScrollTopButton
}: {
  activation: Activation;
  setActivation: (activation: Activation) => any;
  setWithScrollTopButton: (withScrollTopButton: boolean) => any;
}) {
  const dataIdGenerator = getDataIdGenerator('activation-overview');

  const [selectedTab, setSelectedTab] = useState(ActivatePreviewTab.QUERY);
  const [selectedQuery, setSelectedQuery] = useState(activation.draftQuery || activation.query);
  const [activationLastRun, setActivationLastRun] = useState(null);
  const [target, setTarget] = useState(null);
  const [changeMappingDialogConfig, setChangeMappingDialogConfig] = useState<{
    visible: boolean;
    activation?: Activation;
    target?: Target;
    query?: string;
    mapping?: any;
  }>({
    visible: false
  });

  const coordinatedScrollAnchorRef = useRef(null);

  const warehouseId = activation.warehouse.id;

  const { eventTrack } = useAnalyticsTracker();
  const { confirm } = useConfirm();
  const toasterService = useToaster();

  const [isQueryDirty, setIsQueryDirty] = useState(false);

  useCanDeactivate(
    () => {
      return !isQueryDirty
        ? true
        : confirm({
            title: 'Are you sure?',
            body: `Changes in the Query haven't been saved yet. Do you wish to leave and discard the changes?`
          });
    },
    () => {
      return !isQueryDirty;
    }
  );

  useEffect(() => {
    setActivationLastRun(null);

    fetchActivationLastRun();
    fetchTargetDetails();
  }, [activation]);

  useEffect(() => {
    setWithScrollTopButton(false);

    return () => {
      setWithScrollTopButton(true);
    };
  }, []);

  const queryEditorBadgeType = useMemo<QueryEditorBadgeType>(() => {
    if (!activation.query && !selectedQuery) {
      return null;
    }

    if (selectedQuery === activation.draftQuery) {
      return QueryEditorBadgeType.DRAFT_QUERY;
    }

    if (selectedQuery.trim() !== activation.query.trim()) {
      return QueryEditorBadgeType.UNVERIFIED_CHANGES;
    }

    return null;
  }, [selectedQuery, activation.query, activation.draftQuery]);

  const fetchActivationLastRun = () => {
    ActivationsAPI.getActivationRunsResult(activation.id, { limit: 1 }).then(
      resp => {
        if (resp && resp.count) {
          const [rawRun] = resp.data;
          setActivationLastRun(SyncHistoryItemFactory(rawRun));
        }
      },
      () => {}
    );
  };

  const fetchTargetObjectMandatoryFields = () => {
    const targetObjectSettings = activation.targetSyncConfig as GenericTargetObjectSettings;

    return ActivateTargetAPI.getTargetObjectSyncModeIdentifiers(
      activation.target?.id,
      targetObjectSettings.targetObject?.apiName
    )
      .then(syncModeMap => {
        if (syncModeMap && Object.keys(syncModeMap).length) {
          return syncModeMap[targetObjectSettings.syncBehaviour?.value].mandatoryFields || [];
        }

        return [];
      })
      .then(mandatoryFields => {
        return (mandatoryFields || []).filter(mandatoryField => {
          return !(
            target.targetType === TargetTypesEnum.KLAVIYO &&
            targetObjectSettings.targetObject.apiName === 'events' &&
            (targetObjectSettings as KlaviyoTargetObjectSettings)?.eventName &&
            mandatoryField === 'event'
          );
        });
      });
  };

  /**
   * We should use resolver for this
   */
  const fetchTargetDetails = () => {
    ActivateTargetAPI.getTarget(activation.target.seqId).then(
      res => {
        setTarget(TargetFactory(res.data));
      },
      () => {}
    );
  };

  const selectTab = (tab: ActivatePreviewTab) => {
    eventTrack({
      action: TRACKER_ACTIVATION_OVERVIEW_SELECT_TAB_CLICK,
      properties: {
        ...getActivationTrackingProps(),
        index: tab
      }
    });

    setSelectedTab(tab);
  };

  const showChangeMappingDialog = (newMapping: MappingSummary = null) => {
    eventTrack({
      action: TRACKER_ACTIVATION_OVERVIEW_CHANGE_MAPPING_OPEN,
      properties: {
        ...getActivationTrackingProps()
      }
    });

    setChangeMappingDialogConfig({
      visible: true,
      activation,
      target,
      query: selectedQuery.trim(),
      mapping: newMapping
    });
  };

  const onChangeMappingDialogClose = ({ mappingUpdated }) => {
    setChangeMappingDialogConfig({
      visible: false
    });

    if (!mappingUpdated) {
      updateActivationDraftQuery(selectedQuery.trim());
    } else {
      cleanUpAndUpdateActivationQuery();
    }
  };

  const cleanUpAndUpdateActivationQuery = () => {
    activation.draftQuery = null;
    activation.query = selectedQuery.trim();

    setIsQueryDirty(false);

    setActivation(extendObject(new Activation(), activation));
    setSelectedTab(ActivatePreviewTab.QUERY);
  };

  const updateActivationDraftQuery = (draftQuery: string = null) => {
    if (draftQuery === activation.draftQuery) {
      return;
    }

    ActivationsAPI.updateActivation(activation.id, {
      draft_config: { query: draftQuery }
    }).then(() => {
      activation.draftQuery = draftQuery;
      setActivation(extendObject(new Activation(), activation));
      setSelectedTab(ActivatePreviewTab.QUERY);

      setIsQueryDirty(false);

      if (draftQuery) {
        eventTrack({
          action: TRACKER_ACTIVATION_OVERVIEW_SAVE_DRAFT_QUERY_SUCCESS,
          properties: {
            draftQuery
          }
        });
      }
    });
  };

  const getActivationTrackingProps = () => {
    return {
      activationId: activation.id
    };
  };

  const onValidateSaveClick = () => {
    eventTrack({
      action: TRACKER_ACTIVATION_OVERVIEW_SAVE_QUERY_CLICK,
      properties: {
        ...getActivationTrackingProps()
      }
    });

    const currentQuery = selectedQuery.trim();

    Promise.all([
      ActivationsAPI.getQueryMappings(currentQuery, activation.id),
      ACTIVATION_TARGET_META_DATA[activation.target.targetType]?.hasMandatoryFields
        ? fetchTargetObjectMandatoryFields()
        : Promise.resolve([])
    ]).then(([res, targetObjectMandatoryFields]) => {
      if (res?.data) {
        const newMapping = getMappingSummaryFromRawData(res.data, activation.target.targetType);

        const activationTargetMetaData = ACTIVATION_TARGET_META_DATA[target.targetType];

        if (
          activationTargetMetaData.hasIdentifiers ||
          activationTargetMetaData.hasMandatoryFields
        ) {
          const requiredTargetFields = [
            ...((activation.targetSyncConfig as any).uniqueIdentifierKeys || []),
            ...targetObjectMandatoryFields
          ];

          const isRequiredFieldsPresent = requiredTargetFields.every(fieldName =>
            newMapping.fields.find(mappingRow => mappingRow.destination?.name === fieldName)
          );

          if (!isRequiredFieldsPresent) {
            toasterService.pop(
              'error',
              undefined,
              'Query could not be saved: Your modified query does not include the fields required to be mapped.' +
                ' This may cause the Activation to fail.'
            );

            return;
          }
        }

        if (
          newMapping.mappingStatus === MappingStatus.MAPPED &&
          target.targetType !== TargetTypesEnum.GOOGLE_SHEETS
        ) {
          fetchWarehouseFieldsAndUpdateQuery(currentQuery, newMapping.fields);
        } else {
          showChangeMappingDialog(newMapping);
        }
      }
    });
  };

  const fetchWarehouseFieldsAndUpdateQuery = (
    query: string,
    mappingSummaryFields: MappingRow[]
  ) => {
    const schemaData = {
      warehouse_poll_config: {
        mode: 'QUERY',
        query: query
      },
      warehouse_id: warehouseId
    };

    ActivationsAPI.getWarehouseQueryFields(schemaData).then(res => {
      const warehouseFields = res?.data?.warehouse_fields;

      if (warehouseFields?.length) {
        saveQueryAndUpdateActivation(mappingSummaryFields, warehouseFields);
      }
    });
  };

  const saveQueryAndUpdateActivation = (
    mappingSummaryFields: MappingRow[],
    warehouseFields: any[]
  ) => {
    const changedMapping = getUpdateActivationQueryParams(mappingSummaryFields, warehouseFields);

    ActivationsAPI.updateActivation(
      activation.id,
      changedMapping,
      {
        queryUpdated: true
      },
      {
        showLoading: false,
        showSuccessMsg: false
      }
    ).then(() => {
      toasterService.pop('success', undefined, 'Query has been successfully updated!');

      eventTrack({
        action: TRACKER_ACTIVATION_OVERVIEW_SAVE_QUERY_SUCCESS,
        properties: {
          ...getActivationTrackingProps()
        }
      });

      cleanUpAndUpdateActivationQuery();
    });
  };

  const getUpdateActivationQueryParams = (
    mappingSummaryFields: MappingRow[],
    warehouseFields: any[]
  ) => {
    let rawFieldsMapping;

    if (TargetTypesEnum.GOOGLE_SHEETS === target.targetType) {
      rawFieldsMapping = transformMappingRowsToSheetRawData(mappingSummaryFields);
    } else {
      rawFieldsMapping = transformMappingRowsToRawData(mappingSummaryFields, target.targetType);
    }

    return {
      data_mapping: {
        field_mappings: rawFieldsMapping
      },
      warehouse_poll_config: {
        mode: 'QUERY',
        query: selectedQuery.trim()
      },
      draft_config: {
        query: null
      },
      warehouse_query_fields: {
        warehouse_fields: warehouseFields
      }
    };
  };

  const revertToLastSavedQuery = () => {
    eventTrack({
      action: TRACKER_ACTIVATION_OVERVIEW_SAVED_QUERY_REVERT_CLICK,
      properties: {
        ...getActivationTrackingProps()
      }
    });

    updateActivationDraftQuery();
  };

  const hasLastRunFailedRecords =
    activationLastRun &&
    (activationLastRun.transformationRunStats?.recordsFailed ||
      activationLastRun.loadingRunStats?.recordsFailed);

  return (
    <HdCoordinatedScrollAnchor elRef={coordinatedScrollAnchorRef.current}>
      <div>
        <div className='body-container'>
          {hasLastRunFailedRecords ? (
            <LastRunFailedRecordsMessagePane
              className='mb-5'
              activationSeqId={activation.seqId}
              dataId={dataIdGenerator('')}
            />
          ) : null}

          <div
            className='bg-surface border-radius-md border shadow-default overflow-hidden surface-on-surface surface-within-card'
          >
            <div className='center-flex-row justify-between p-5'>
              <div className='btn-group btn-group-toggle'>
                <HdButton
                  dataId={dataIdGenerator('query-tab')}
                  variation='outline'
                  onClick={() => selectTab(ActivatePreviewTab.QUERY)}
                  active={selectedTab === ActivatePreviewTab.QUERY}
                >
                  Query
                </HdButton>

                <HdButton
                  dataId={dataIdGenerator('query-tab')}
                  variation='outline'
                  onClick={() => selectTab(ActivatePreviewTab.MAPPING)}
                  active={selectedTab === ActivatePreviewTab.MAPPING}
                >
                  Mapping
                </HdButton>
              </div>

              <div className='center-flex-row pl-5'>
                <NodeLogo
                  className='mr-3'
                  roundedBorders
                  logoURL={activation.target?.targetTypeLogoUrl}
                  darkModeLogoURL={activation.target?.targetTypeDarkModeLogoUrl}
                  primaryColor={TARGET_TYPES[activation.target?.targetType].primaryColor}
                  darkModePrimaryColor={
                    TARGET_TYPES[activation.target?.targetType].darkModePrimaryColor
                  }
                />

                <ActivationOverviewHeaderTarget activation={activation} target={target} />
              </div>
            </div>

            {queryEditorBadgeType === QueryEditorBadgeType.DRAFT_QUERY ? (
              <DraftQueryMessagePane
                dataId={dataIdGenerator('')}
                activation={activation}
                selectedTab={selectedTab}
                selectTab={selectTab}
                showChangeMappingDialog={showChangeMappingDialog}
              />
            ) : null}

            {selectedTab === ActivatePreviewTab.QUERY ? (
              <ActivationPreviewWorkbench
                activation={activation}
                savedQuery={activation.query}
                currentQuery={selectedQuery}
                queryEditorBadgeType={queryEditorBadgeType}
                revertToSavedQuery={revertToLastSavedQuery}
                validateAndSaveClick={onValidateSaveClick}
                updateQueryState={isDirty => setIsQueryDirty(isDirty)}
                onQueryChange={q => setSelectedQuery(q)}
              />
            ) : null}

            {selectedTab === ActivatePreviewTab.MAPPING ? (
              <WebComponentRenderer
                elementSelector='update-activation-mapping-web-component'
                {...({
                  activation,
                  target,
                  onActivationUpdate: a => {
                    setActivation(extendObject(new Activation(), a));
                    setSelectedTab(ActivatePreviewTab.QUERY);
                  }
                } as UpdateActivationMappingAngularInReactProps)}
              >
                {/* @ts-ignore */}
                <update-activation-mapping-web-component></update-activation-mapping-web-component>
              </WebComponentRenderer>
            ) : null}
          </div>
        </div>

        <ChangeMappingDialog
          config={changeMappingDialogConfig}
          onClose={onChangeMappingDialogClose}
        />
      </div>
    </HdCoordinatedScrollAnchor>
  );
}
